import { Callout } from "@bednbody/ui/components/callout";
import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import { Checkbox } from "@bednbody/ui/components/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Input } from "@bednbody/ui/components/input";
import { RadioGroup, RadioGroupItem } from "@bednbody/ui/components/radio-group";
import { Spinner } from "@bednbody/ui/components/spinner";
import { Textarea } from "@bednbody/ui/components/textarea";
import { cn } from "@bednbody/ui/lib/utils";
import { WarningIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import {
  type TherapistSessionTodayRow,
  useOffTherapistsForDate,
  useTherapistCycleWeek,
} from "./therapist-sessions.hooks";

const WEEKDAY = new Intl.DateTimeFormat("en-PH", { weekday: "short", timeZone: "Asia/Manila" });

/** `YYYY-MM-DD` → "Wed 7 May", without dragging a date library in. */
function dayLabel(date: string): string {
  const d = new Date(`${date}T12:00:00+08:00`);
  return `${WEEKDAY.format(d)} ${d.getDate()}`;
}

/**
 * ISO instant → `datetime-local`'s value shape, in the BROWSER's own
 * timezone — same convention `BookingDetailActions.tsx` uses for its
 * `corrected-start` field: `datetime-local` carries no offset, so the
 * round-trip back through `new Date(value).toISOString()` on submit is what
 * actually fixes the instant, not this formatting step.
 */
function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * ⚠ `void` HERE IS NOT THE RIDER-SIDE VOID, AND THE TWO ARE NOT SYMMETRICAL
 * UNDER THE HOOD. The word is shared so operators have one vocabulary across
 * both screens (Leon's call, 2026-09-04) — but:
 *
 *   · Rider void (`admin_void_rider_session`, 0196) applies ONLY to manually
 *     created supply. It sets `voided_at`, clears ownership, and makes the row
 *     stop being supply — a state that exists because 0196 created rows that can
 *     stop existing as supply.
 *   · Therapist "void" is `state = 'off_final'`, the ordinary Off state. There
 *     is no fifth `therapist_date_state` and there must not be: 0074 creates all
 *     seven date rows per therapist per cycle, so her row for this date already
 *     exists and only its state can move. Off IS the removal.
 *
 * So this mode maps to `off_final` and nothing else. If a future reader is
 * tempted to add a `voided` therapist state to "match" the rider side: don't —
 * every body that switches on `therapist_date_state` would have to exclude it,
 * and missing one is 0140's failure mode.
 */
type Mode = "secure" | "void" | "reassign" | "retime";

interface TherapistSessionOverrideDialogProps {
  session: TherapistSessionTodayRow | null;
  /** Superadmin only (0245) — hide the option rather than offer a 403. */
  canRetime: boolean;
  serviceDate: string;
  isPending: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onSetState: (input: { state: "secured_final" | "off_final"; reason: string }) => void;
  onOffDays: (input: { dates: string[]; reason: string }) => void;
  onReassign: (input: { toTherapistId: string; reason: string }) => void;
  /** 0245 — Superadmin only; the server 403s an Admin caller. */
  onRetime: (input: { startsAt: string; endsAt: string; reason: string }) => void;
}

/**
 * OQ-122 — move one finalized Therapist date.
 *
 * ═══ THE ONE THING THIS DIALOG MUST NOT LET SOMEONE BELIEVE ═══
 *
 * That putting a Therapist on a date changes how many Riders exist. It does not.
 * `therapist_final_counts` was locked at Friday 7:00 PM and is immutable, and
 * CORE-05A is explicit that a later change "must never rewrite the locked count
 * or regenerate Rider Sessions". So an operator who adds a Therapist at 2 PM has
 * added a Therapist and NOT a Rider to carry her, and the note below says so —
 * because the alternative is discovering it when nobody turns up to drive.
 *
 * The three actions are one dialog because they are one decision with three
 * shapes: is this Therapist working this date, and if not, who is instead.
 */
export function TherapistSessionOverrideDialog({
  session,
  canRetime,
  serviceDate,
  isPending,
  errorMessage,
  onClose,
  onSetState,
  onOffDays,
  onReassign,
  onRetime,
}: TherapistSessionOverrideDialogProps) {
  const isSecured = session?.state === "secured_final";
  const [mode, setMode] = useState<Mode>("void");
  const [therapistId, setTherapistId] = useState("");
  const [reason, setReason] = useState("");
  /** Extra dates beyond the one clicked. The clicked date is always included. */
  const [alsoDates, setAlsoDates] = useState<string[]>([]);
  const [retimeStartsAt, setRetimeStartsAt] = useState("");
  const [retimeEndsAt, setRetimeEndsAt] = useState("");

  const { data: offTherapists, isLoading: offLoading } = useOffTherapistsForDate(
    serviceDate,
    session?.hubId ?? null,
  );
  const { data: week } = useTherapistCycleWeek(
    session?.cycleId ?? null,
    session?.therapistId ?? null,
  );

  // A fresh Session is a fresh decision — otherwise the previous row's chosen
  // therapist, dates and reason survive into this one.
  useEffect(() => {
    if (session) {
      setMode(session.state === "secured_final" ? "void" : "secure");
      setTherapistId("");
      setReason("");
      setAlsoDates([]);
      setRetimeStartsAt(toDatetimeLocal(session.startsAt));
      setRetimeEndsAt(toDatetimeLocal(session.endsAt));
    }
  }, [session?.id]);

  const now = Date.now();
  /**
   * Her OTHER dates in the cycle. Every one is listed; the ones 0197 would
   * refuse are disabled with the reason rather than hidden — an all-or-nothing
   * call that fails on an invisible date reads as a broken button.
   */
  const otherDays = (week ?? [])
    .filter((d) => d.id !== session?.id)
    .map((d) => ({
      date: d.service_date,
      selectable: d.state === "secured_final" && !d.started_at && Date.parse(d.ends_at) > now,
      note:
        d.state !== "secured_final"
          ? "already Off"
          : d.started_at
            ? "already started"
            : Date.parse(d.ends_at) <= now
              ? "window closed"
              : null,
    }));

  const selectedDates = session ? [session.serviceDate, ...alsoDates].sort() : [];

  const options = (offTherapists ?? []).filter((t) => t.therapistId !== session?.therapistId);
  const trimmedReason = reason.trim();
  const retimeValid =
    retimeStartsAt !== "" &&
    retimeEndsAt !== "" &&
    new Date(retimeEndsAt) > new Date(retimeStartsAt);
  const canSubmit =
    trimmedReason.length > 0 &&
    !isPending &&
    (mode === "reassign" ? therapistId !== "" : mode === "retime" ? retimeValid : true);

  const actions: ReadonlyArray<readonly [Mode, string, string]> = isSecured
    ? [
        [
          "void",
          "Void this date",
          // Says what actually happens, not just what the button is called —
          // this is the ordinary Off state, not the rider side's voided row.
          "Her date becomes Off and nobody replaces her. The row stays; only its state moves.",
        ],
        [
          "reassign",
          "Hand the date to someone else",
          "One transaction: she gives it up, the other takes it. The date's count is unchanged.",
        ],
        ...(canRetime
          ? ([
              [
                "retime",
                "Move today's start/end time",
                "Change when this Session runs, still today — the lever for a booking window the fixed 2:00 PM start blocks. Superadmin only.",
              ],
            ] as const)
          : []),
      ]
    : [["secure", "Put her on this date", "Her Off date becomes Secured. She can then Start it."]];

  return (
    <Dialog
      open={session !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Move a Therapist date</DialogTitle>
          <DialogDescription>
            {session ? (
              <>
                {session.therapistName} · {serviceDate} · {session.hubName}
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs">
            <p className="text-muted-foreground">
              Currently{" "}
              <span className="font-medium text-foreground">
                {isSecured ? "Secured — she holds this date" : "Off — she does not hold this date"}
              </span>
            </p>
            <p className="mt-1 text-muted-foreground">
              Her row for this date already exists; only its state moves. No date is created or
              deleted.
            </p>
          </div>

          {actions.length > 1 ? (
            <fieldset>
              <legend className="mb-2 block text-sm font-medium">Action</legend>
              <RadioGroup value={mode} onValueChange={(value) => setMode(value as Mode)}>
                {actions.map(([value, label, hint]) => (
                  <label
                    key={value}
                    htmlFor={`tmode-${value}`}
                    className={cn(
                      "flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2 transition-colors",
                      mode === value ? "border-primary bg-primary/5" : "border-border",
                    )}
                  >
                    <RadioGroupItem id={`tmode-${value}`} value={value} className="mt-0.5" />
                    <span>
                      <span className="block text-sm font-medium">{label}</span>
                      <span className="block text-xs text-muted-foreground">{hint}</span>
                    </span>
                  </label>
                ))}
              </RadioGroup>
            </fieldset>
          ) : null}

          {mode === "void" && otherDays.length > 0 ? (
            <fieldset>
              <legend className="mb-2 block text-sm font-medium">
                Also take her off these dates
              </legend>
              <div className="space-y-1.5">
                {otherDays.map((day) => {
                  const checked = alsoDates.includes(day.date);
                  return (
                    <label
                      key={day.date}
                      htmlFor={`day-${day.date}`}
                      className={cn(
                        "flex items-center gap-3 rounded-md border px-3 py-2 transition-colors",
                        day.selectable ? "cursor-pointer" : "cursor-not-allowed opacity-60",
                        checked ? "border-primary bg-primary/5" : "border-border",
                      )}
                    >
                      <Checkbox
                        id={`day-${day.date}`}
                        checked={checked}
                        disabled={!day.selectable}
                        onCheckedChange={(next) =>
                          setAlsoDates((prev) =>
                            next === true
                              ? [...prev, day.date]
                              : prev.filter((d) => d !== day.date),
                          )
                        }
                      />
                      <span className="flex-1 text-sm">{dayLabel(day.date)}</span>
                      {day.note ? (
                        <span className="text-xs text-muted-foreground">{day.note}</span>
                      ) : null}
                    </label>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                {dayLabel(serviceDate)} is always included. All selected dates move together — if
                one cannot, none do.
              </p>
            </fieldset>
          ) : null}

          {mode === "reassign" ? (
            <fieldset>
              <legend className="mb-2 block text-sm font-medium">Give the date to</legend>
              {offLoading ? (
                <div className="flex items-center gap-2 px-1 py-2 text-sm text-muted-foreground">
                  <Spinner className="size-4" />
                  Loading Therapists who are Off this date…
                </div>
              ) : options.length === 0 ? (
                <p className="rounded-md border border-border px-3 py-2 text-sm text-muted-foreground">
                  Nobody in this Dispatch Area holds an Off Session on this date, so there is nobody
                  to hand it to. Only a Therapist already in the cycle with an Off date can take it.
                </p>
              ) : (
                <RadioGroup value={therapistId} onValueChange={setTherapistId}>
                  <div className="max-h-52 space-y-1.5 overflow-y-auto pr-1">
                    {options.map((t) => (
                      <label
                        key={t.therapistId}
                        htmlFor={`therapist-${t.therapistId}`}
                        className={cn(
                          "flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 transition-colors",
                          therapistId === t.therapistId
                            ? "border-primary bg-primary/5"
                            : "border-border",
                        )}
                      >
                        <RadioGroupItem id={`therapist-${t.therapistId}`} value={t.therapistId} />
                        <span className="min-w-0 flex-1 truncate text-sm">{t.fullName}</span>
                        <Badge variant="outline" className="shrink-0 capitalize">
                          {t.rank}
                        </Badge>
                      </label>
                    ))}
                  </div>
                </RadioGroup>
              )}
            </fieldset>
          ) : null}

          {mode === "retime" ? (
            <fieldset className="space-y-3">
              <legend className="mb-2 block text-sm font-medium">New window</legend>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label
                    htmlFor="retime-starts"
                    className="block text-xs font-medium text-muted-foreground"
                  >
                    New start
                  </label>
                  <Input
                    id="retime-starts"
                    type="datetime-local"
                    value={retimeStartsAt}
                    onChange={(e) => setRetimeStartsAt(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <label
                    htmlFor="retime-ends"
                    className="block text-xs font-medium text-muted-foreground"
                  >
                    New end
                  </label>
                  <Input
                    id="retime-ends"
                    type="datetime-local"
                    value={retimeEndsAt}
                    onChange={(e) => setRetimeEndsAt(e.target.value)}
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                The new start must stay on {dayLabel(serviceDate)} — the server refuses a different
                day (CORE-05B §4.9.4 still forbids moving a Session's date, only its time). Maximum
                window is 24 hours. Refused once she has tapped Start.
              </p>
            </fieldset>
          ) : null}

          <div className="space-y-2">
            <label htmlFor="therapist-override-reason" className="block text-sm font-medium">
              Reason (required)
            </label>
            <Textarea
              id="therapist-override-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this date moving? e.g. called in sick; cover arranged."
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Written to the audit log with your name, and to the cycle&rsquo;s event history.
            </p>
          </div>

          {/* The consequence an operator cannot see from this screen, stated on
              the control that causes it. */}
          {mode === "secure" ? (
            <Callout tone="info">
              This adds a Therapist, not a Rider. The Final Therapist Count was locked at Friday
              7:00 PM and never changes, so Saturday&rsquo;s Rider supply for this date has already
              been generated without her.
            </Callout>
          ) : null}

          <p className="flex gap-2 rounded-lg border border-warning/30 bg-warning/5 px-3 py-2 text-xs text-warning">
            <WarningIcon weight="fill" className="mt-px size-4 shrink-0" />
            <span>
              {mode === "retime" ? (
                <>
                  Manual override, Superadmin only. This is a separate authorization from the
                  Secured/Off/Hand-over actions above — CORE-05B §4.9.4 excludes changing a
                  Session's time from OQ-122, so this exists by its own decision, not that one.
                  Every use is audited.
                </>
              ) : (
                <>
                  Manual override. A finalized week normally moves only by Trade between two
                  Therapists; this exists by client decision (OQ-122) and every use is audited.
                </>
              )}
            </span>
          </p>

          {errorMessage ? <p className="text-sm text-destructive">{errorMessage}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant={mode === "void" ? "destructive" : "default"}
            disabled={!canSubmit}
            onClick={() => {
              if (mode === "reassign") {
                return onReassign({ toTherapistId: therapistId, reason: trimmedReason });
              }
              if (mode === "secure") {
                return onSetState({ state: "secured_final", reason: trimmedReason });
              }
              if (mode === "retime") {
                return onRetime({
                  startsAt: new Date(retimeStartsAt).toISOString(),
                  endsAt: new Date(retimeEndsAt).toISOString(),
                  reason: trimmedReason,
                });
              }
              // Void always goes through the multi-date endpoint, even for a
              // single date. One code path means the all-or-nothing guarantee
              // and the audit shape do not depend on how many boxes were ticked.
              return onOffDays({ dates: selectedDates, reason: trimmedReason });
            }}
          >
            {isPending
              ? "Saving…"
              : mode === "reassign"
                ? "Hand over the date"
                : mode === "secure"
                  ? "Put her on"
                  : mode === "retime"
                    ? "Save new time"
                    : selectedDates.length > 1
                      ? `Void ${selectedDates.length} dates`
                      : "Void date"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
